import type { Transition } from "motion/react";
import { duration, easing } from "@/lib/animation/tokens";

/**
 * Motion rules for `<BeerCardInteractive>`. Two interactions:
 *
 *  - Mouse push-away — soft cursor-eased translate via spring (when the
 *    card is face-up).
 *  - Card flip — playing-card-style 3D rotation around the Y axis on
 *    click, landing with a small Z tilt so the back doesn't sit
 *    perfectly square. The tilt is re-rolled randomly on every flip
 *    (`randomSettleTilt`) so a row of flipped cards looks casually
 *    scattered rather than identically aligned, and repeat-flipping the
 *    same card doesn't keep landing at the same angle.
 */

/* -------------------------- Mouse push-away -------------------------- */

/**
 * Spring used to settle the mouse-push translate. Tuned for "drifty",
 * not "snappy" — high damping so it never overshoots, low stiffness so
 * it lags behind the cursor a touch.
 */
export const mousePushSpring = {
  type: "spring" as const,
  stiffness: 110,
  damping: 26,
  mass: 0.7,
};

/** Maximum cursor-push distance (px) on either axis at the card edge. */
export const MOUSE_PUSH_MAX = 6;

/* ----------------------------- Card flip ----------------------------- */

/** Transition used by both the flip-to-back and flip-to-front tweens. */
export const flipTransition: Transition = {
  duration: duration.lg,
  ease: easing.out,
};

/**
 * Box-shadows for the flip layer. The shadow lives on the rotating
 * element so it follows the card (otherwise it sits as a ghost
 * rectangle in the card's original position).
 *
 * Two stable values, linearly interpolated over the flip — no peak.
 * Around the 90° mark the layer is edge-on to the viewer, so any
 * shadow there is invisible by construction; trying to peak the
 * intensity there is wasted. The shadow simply grows from `rest` to
 * `settled` as the card lifts, and the flipped resting state lands
 * with a deeper shadow because the card is tilted (per `settleTiltFor`).
 */
const restShadow = "0 1px 3px rgba(0, 0, 0, 0.10)";
const settledShadow = "0 10px 22px rgba(0, 0, 0, 0.22)";

export const cardShadow = {
  rest: restShadow,
  settled: settledShadow,
};

/**
 * Random settle tilt (degrees) for a flipped card. Pick a fresh value
 * each time the card flips so repeat-flipping the same card looks
 * organic rather than landing at the same angle every time.
 *
 * Magnitude is constrained to 2°–5° (with random sign) so the tilt is
 * always noticeable but never extreme — the band excludes the dead
 * zone near 0° where a tilt would barely read.
 */
export function randomSettleTilt(): number {
  const magnitude = 2 + Math.random() * 3; // 2..5
  return Math.random() < 0.5 ? -magnitude : magnitude;
}
